import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  createColumnHelper,
  flexRender,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { apiFetch } from '../utils/apiUtils';
import PassportViewer from './PassportViewer';
import TicketActionModal from './TicketActionModal';
import AddPassengerModal from './AddPassengerModal';

const columnHelper = createColumnHelper();

const CSV_COLS = [
  'first_name', 'last_name', 'email', 'phone', 'birth_date', 'gender', 'school',
  'passport_number', 'passport_country', 'passport_expiration',
  'route', 'flight_id', 'bags_count', 'access_code', 'amount_charged',
  'has_infant', 'infant_first_name', 'infant_last_name', 'infant_birth_date',
  'infant_gender', 'infant_passport_number', 'infant_passport_country', 'infant_passport_expiration',
  'created_at', 'stripe_payment_status', 'tariff_paid'
];

function exportCsv(rows, filename) {
  const escape = (v) => {
    const s = v == null ? '' : String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = CSV_COLS.join(',');
  const body = rows.map(r => CSV_COLS.map(c => escape(r[c])).join(',')).join('\n');
  const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function ExportButton({ rows, filename, title }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); exportCsv(rows, filename); }}
      title={title || `Export ${filename}`}
      className="inline-flex items-center ml-1 p-0.5 text-gray-400 hover:text-blue-600 rounded transition-colors"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
        <path d="M10.75 2.75a.75.75 0 00-1.5 0v8.614L6.295 8.235a.75.75 0 10-1.09 1.03l4.25 4.5a.75.75 0 001.09 0l4.25-4.5a.75.75 0 00-1.09-1.03l-2.955 3.129V2.75z" />
        <path d="M3.5 12.75a.75.75 0 00-1.5 0v2.5A2.75 2.75 0 004.75 18h10.5A2.75 2.75 0 0018 15.25v-2.5a.75.75 0 00-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5z" />
      </svg>
    </button>
  );
}

function FlightAdmin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [authenticated, setAuthenticated] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [selectedPassport, setSelectedPassport] = useState(null);
  const [showPassportModal, setShowPassportModal] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('succeeded');
  const [flightFilter, setFlightFilter] = useState('');
  const [dedupeByPassport, setDedupeByPassport] = useState(false);
  const [showWaitlistQueue, setShowWaitlistQueue] = useState(false);
  const [waitlistSearch, setWaitlistSearch] = useState('');
  const [editingReg, setEditingReg] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [ticketActionReg, setTicketActionReg] = useState(null);
  const [showAddPassenger, setShowAddPassenger] = useState(false);

  // Get access code from query params
  const accessCode = searchParams.get('access_code');
  
  // Function to update access code in URL
  const updateAccessCode = (newCode) => {
    setSearchParams({ access_code: newCode });
  };
  
  // Authentication query
  const authQuery = useQuery({
    queryKey: ['flightAuth', accessCode],
    queryFn: async () => {
      if (!accessCode) {
        throw new Error('Please provide an access code.');
      }
      console.log('Verifying flight admin access code:', accessCode);
      return apiFetch(`${import.meta.env.VITE_API_URL}/flight-admin/verify?access_code=${accessCode}`);
    },
    enabled: !!accessCode,
    retry: false
  });

  // Update authenticated state when query completes
  useEffect(() => {
    if (authQuery.isSuccess && authQuery.data) {
      const data = authQuery.data;
      console.log('Flight admin auth success response:', data);
      if (data.status === 'success' && data.authenticated) {
        setAuthenticated(true);
        setAuthError(null);
        setUserInfo(data.user || null);
      } else {
        setAuthenticated(false);
        setAuthError('Invalid access code. Please try again.');
        setUserInfo(null);
      }
    } else if (authQuery.isError) {
      console.error('Flight admin authentication error:', authQuery.error);
      setAuthenticated(false);
      setAuthError(`Authentication error: ${authQuery.error.message}`);
    }
  }, [authQuery.isSuccess, authQuery.isError, authQuery.data, authQuery.error]);

  // Flight registrations query
  const registrationsQuery = useQuery({
    queryKey: ['flightRegistrations', accessCode],
    queryFn: async () => {
      console.log('Fetching flight registrations...');
      return apiFetch(`${import.meta.env.VITE_API_URL}/flight-admin/registrations?access_code=${accessCode}`);
    },
    enabled: authenticated,
    retry: 2,
    retryDelay: 3000,
    staleTime: 60000, // 1 minute
    cacheTime: 300000, // 5 minutes
    onError: (error) => {
      console.error('Error fetching flight registrations:', error);
      toast.error(`Failed to load registrations: ${error.message}`);
    }
  });

  // Build school ID-to-name map for resolving old registrations that stored IDs
  const schoolIdMap = useMemo(() => {
    if (!registrationsQuery.data?.schools) return {};
    const map = {};
    registrationsQuery.data.schools.forEach(s => { map[s.id] = s.name; });
    return map;
  }, [registrationsQuery.data?.schools]);

  // Resolve school value (could be name or numeric ID)
  const resolveSchool = (school) => {
    if (!school) return '';
    if (/^\d+$/.test(school) && schoolIdMap[school]) return schoolIdMap[school];
    return school;
  };

  // Get unique schools for filter dropdown
  const schools = useMemo(() => {
    if (!registrationsQuery.data?.registrations) return [];
    const uniqueSchools = [...new Set(registrationsQuery.data.registrations.map(reg => resolveSchool(reg.school)).filter(Boolean))];
    return uniqueSchools.sort();
  }, [registrationsQuery.data?.registrations, schoolIdMap]);

  // Get unique status values for filter dropdown
  const statusOptions = useMemo(() => {
    if (!registrationsQuery.data?.registrations) return [];
    const uniqueStatuses = [...new Set(registrationsQuery.data.registrations.map(reg => reg.admin_status || 'pending'))];
    return uniqueStatuses.sort();
  }, [registrationsQuery.data?.registrations]);

  // Get unique payment status values for filter dropdown
  const paymentStatusOptions = useMemo(() => {
    if (!registrationsQuery.data?.registrations) return [];
    const uniquePaymentStatuses = [...new Set(registrationsQuery.data.registrations.map(reg => reg.stripe_payment_status || 'pending'))];
    const sorted = uniquePaymentStatuses.sort();
    // Add cancel/refund virtual statuses for filtering
    return [...sorted, 'cancel-confirmed', 'cancel-pending', 'refundable'];
  }, [registrationsQuery.data?.registrations]);

  // Get non-deleted registrations for statistics
  const activeRegistrations = useMemo(() => {
    if (!registrationsQuery.data?.registrations) return [];
    return registrationsQuery.data.registrations.filter(reg => (reg.admin_status || 'pending') !== 'delete');
  }, [registrationsQuery.data?.registrations]);

  const codeCounts = registrationsQuery.data?.codeCounts ?? {};
  const f1Codes = codeCounts['flight-1'] ?? { waitlist: 0, reserved: 0, reservedTotal: 0 };
  const f2Codes = codeCounts['flight-2'] ?? { waitlist: 0, reserved: 0, reservedTotal: 0 };
  const f3Codes = codeCounts['flight-3'] ?? { waitlist: 0, reserved: 0, reservedTotal: 0 };
  const multiFlightCodes = codeCounts['unknown'] ?? { waitlist: 0, reserved: 0, reservedTotal: 0 };
  const flightConfigs = registrationsQuery.data?.flightConfigs ?? {};
  const f1Config = flightConfigs['flight-1'] ?? { totalCapacity: 130, publicCapacity: 65, nycCapacity: 40 };
  const f2Config = flightConfigs['flight-2'] ?? { totalCapacity: 150, publicCapacity: 0, nycCapacity: 120 };
  const f3Config = flightConfigs['flight-3'] ?? { totalCapacity: 180, publicCapacity: 0, nycCapacity: 90, milanCapacity: 90 };

  // "Still Looking" stats: unique passports that registered but have no payment on ANY flight
  // Only counts registrations from March 17 onwards (all flights closed before that)
  const stillLookingStats = useMemo(() => {
    if (!activeRegistrations.length) return { total: 0, committedToBook: 0 };

    const cutoffTime = new Date('2026-03-17T00:00:00+02:00'); // midnight IST March 17
    const excludedStatuses = ['delete', 'cancel-confirmed', 'cancel-pending'];
    const validRegs = activeRegistrations.filter(r =>
      !excludedStatuses.includes(r.admin_status) && new Date(r.created_at) >= cutoffTime
    );

    // Collect all passport numbers that have a successful payment on any flight
    const paidPassports = new Set();
    for (const r of activeRegistrations) {
      if (r.stripe_payment_status === 'succeeded') {
        const key = (r.passport_number || '').toLowerCase().trim();
        if (key) paidPassports.add(key);
      }
    }

    // Unpaid registrations, deduped by passport (keep most recent per passport)
    const unpaid = validRegs.filter(r => r.stripe_payment_status !== 'succeeded');
    const seenPassports = new Map();
    for (const reg of unpaid.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))) {
      const key = (reg.passport_number || '').toLowerCase().trim();
      if (key && !paidPassports.has(key) && !seenPassports.has(key)) {
        seenPassports.set(key, reg);
      } else if (!key && !seenPassports.has(`id:${reg.id}`)) {
        seenPassports.set(`id:${reg.id}`, reg);
      }
    }

    const dedupedList = Array.from(seenPassports.values());
    return {
      total: dedupedList.length,
      committedToBook: dedupedList.filter(r => r.commit_to_book).length,
    };
  }, [activeRegistrations]);

  // Build waitlist queue from registrations + waitlistCodes
  const waitlistQueue = useMemo(() => {
    if (!activeRegistrations.length) return [];
    const wlCodes = registrationsQuery.data?.waitlistCodes || [];

    // Build a map of regId -> code info
    const codeByRegId = {};
    for (const c of wlCodes) {
      // If multiple codes for same reg, keep the latest
      if (!codeByRegId[c.regId] || c.code > codeByRegId[c.regId].code) {
        codeByRegId[c.regId] = c;
      }
    }

    // Hard cutoff: all flights closed — only count registrations from March 17 onwards as waitlist
    const cutoffTime = new Date('2026-03-17T00:00:00+02:00'); // midnight IST March 17

    // Filter to unpaid registrations after the cutoff
    const unpaid = activeRegistrations.filter(
      r => r.stripe_payment_status !== 'succeeded' && new Date(r.created_at) > cutoffTime
    );

    // Dedupe by passport_number (keep earliest per passport for queue ordering)
    const seen = new Map();
    const sorted = [...unpaid].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    for (const reg of sorted) {
      const key = (reg.passport_number || '').toLowerCase().trim() || `id:${reg.id}`;
      if (!seen.has(key)) seen.set(key, reg);
    }
    const deduped = Array.from(seen.values());

    // Sort: committed first, then by created_at ASC
    deduped.sort((a, b) => {
      if (a.commit_to_book !== b.commit_to_book) return b.commit_to_book ? 1 : -1;
      return new Date(a.created_at) - new Date(b.created_at);
    });

    // Attach queue position and code info, filter by selected flight tab
    return deduped.map((reg, idx) => {
      const code = codeByRegId[reg.id];
      let codeStatus = null;
      if (code) {
        if (code.isUsed) codeStatus = 'paid';
        else if (code.isDeclined) codeStatus = 'declined';
        else if (code.isExpired) codeStatus = 'expired';
        else codeStatus = 'active';
      }
      return {
        position: idx + 1,
        id: reg.id,
        firstName: reg.first_name,
        lastName: reg.last_name,
        email: reg.email,
        passportNumber: reg.passport_number || '',
        accessCode: reg.access_code || '',
        route: reg.route,
        flightId: reg.flight_id,
        committedToBook: reg.commit_to_book,
        createdAt: reg.created_at,
        code: code?.code || null,
        codeFlightId: code?.flightId || null,
        codeStatus,
        codeExpiresAt: code?.expiresAt || null,
      };
    });
  }, [activeRegistrations, registrationsQuery.data?.waitlistCodes]);

  // Get deleted registrations count
  const deletedCount = useMemo(() => {
    if (!registrationsQuery.data?.registrations) return 0;
    return registrationsQuery.data.registrations.filter(reg => reg.admin_status === 'delete').length;
  }, [registrationsQuery.data?.registrations]);

  // Determine which cancel-confirmed registrations are refundable (seat was resold, FCFS order)
  const refundableRegIds = useMemo(() => {
    const ids = new Set();
    if (!activeRegistrations.length) return ids;

    const routeFilter = {
      milan: r => r.route === 'jerusalem-milan' || r.route === 'milan',
      nyc: r => r.route === 'jerusalem-newyork' || r.route === 'newyork',
    };

    for (const flightId of ['flight-1', 'flight-2', 'flight-3']) {
      const config = flightConfigs[flightId];
      if (!config) continue;

      const flightPaid = activeRegistrations.filter(
        r => r.flight_id === flightId && r.stripe_payment_status === 'succeeded'
      );

      const routeCaps = {
        milan: config.milanCapacity ?? (config.totalCapacity - config.nycCapacity),
        nyc: config.nycCapacity,
      };

      for (const [routeKey, filterFn] of Object.entries(routeFilter)) {
        const routePaid = flightPaid.filter(filterFn);
        const cap = routeCaps[routeKey];
        const resold = Math.max(0, routePaid.length - cap);

        if (resold > 0) {
          const cancelled = routePaid
            .filter(r => r.admin_status === 'cancel-confirmed')
            .sort((a, b) => new Date(a.cancel_confirmed_at) - new Date(b.cancel_confirmed_at));

          for (let i = 0; i < Math.min(resold, cancelled.length); i++) {
            ids.add(cancelled[i].id);
          }
        }
      }
    }

    return ids;
  }, [activeRegistrations, flightConfigs]);

  // Route display labels
  const routeLabels = {
    'jerusalem-milan': 'Jerusalem → Milan',
    'jerusalem-newyork': 'Jerusalem → NYC',
    'milan': 'Jerusalem → Milan',
    'newyork': 'Jerusalem → NYC',
  };

  const routePricesByFlight = {
    'flight-1': { 'jerusalem-milan': 950, 'jerusalem-newyork': 1900, 'milan': 950, 'newyork': 1900 },
    'flight-2': { 'jerusalem-milan': 990, 'jerusalem-newyork': 1960, 'milan': 990, 'newyork': 1960 },
    'flight-3': { 'jerusalem-milan': 1050, 'jerusalem-newyork': 2050, 'milan': 1050, 'newyork': 2050 },
  };

  const INFANT_SURCHARGE = 200;

  const flightLabels = {
    'flight-1': 'F1',
    'flight-2': 'F2',
    'flight-3': 'F3',
  };

  // Define columns for the table
  const columns = useMemo(() => [
    columnHelper.accessor('id', {
      header: 'ID',
      size: 60,
    }),
    columnHelper.accessor('flight_id', {
      header: 'Flight',
      size: 60,
      cell: ({ getValue }) => {
        const fid = getValue();
        const label = flightLabels[fid] || fid || '-';
        const colorClass = fid === 'flight-1' ? 'bg-sky-100 text-sky-800' : fid === 'flight-2' ? 'bg-amber-100 text-amber-800' : fid === 'flight-3' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-800';
        return <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${colorClass}`}>{label}</span>;
      },
    }),
    columnHelper.accessor('registration_code', {
      header: 'Code',
      size: 80,
    }),
    columnHelper.accessor('stripe_payment_status', {
      header: 'Payment',
      size: 130,
      enableSorting: true,
      cell: ({ getValue, row }) => {
        const status = getValue();
        const adminStatus = row.original.admin_status;

        // Show cancel status badges for cancel-pending / cancel-confirmed
        if (adminStatus === 'cancel-confirmed') {
          const confirmedAt = row.original.cancel_confirmed_at;
          const tip = confirmedAt ? `Confirmed: ${new Date(confirmedAt).toLocaleString()}` : '';
          const canRefund = refundableRegIds.has(row.original.id);
          return (
            <div className="flex flex-col gap-1">
              <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800 cursor-help" title={tip}>
                CANCELLED
              </span>
              {canRefund && (
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-800">
                  REFUNDABLE
                </span>
              )}
            </div>
          );
        }
        if (adminStatus === 'cancel-pending') {
          return (
            <span className="px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800">
              CANCEL-PENDING
            </span>
          );
        }

        if (!status || status === 'pending') {
          return <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">UNPAID</span>;
        }

        const statusColors = {
          'succeeded': 'bg-green-100 text-green-800',
          'processing': 'bg-blue-100 text-blue-800',
          'completed': 'bg-green-100 text-green-800',
          'failed': 'bg-red-100 text-red-800',
          'refunded': 'bg-yellow-100 text-yellow-800'
        };

        const colorClass = statusColors[status.toLowerCase()] || 'bg-gray-100 text-gray-800';

        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${colorClass}`}>
            {status.toUpperCase()}
          </span>
        );
      },
    }),
    columnHelper.accessor('first_name', {
      header: 'First Name',
      size: 120,
    }),
    columnHelper.accessor('last_name', {
      header: 'Last Name',
      size: 120,
    }),
    columnHelper.accessor('gender', {
      header: 'Gender',
      size: 70,
      cell: ({ getValue }) => {
        const g = getValue();
        return g ? g.charAt(0).toUpperCase() : '';
      },
    }),
    columnHelper.accessor('email', {
      header: 'Email',
      size: 200,
    }),
    columnHelper.accessor('phone', {
      header: 'Phone',
      size: 140,
    }),
    columnHelper.accessor('school', {
      header: 'School',
      size: 200,
      filterFn: 'includesString',
      cell: ({ getValue }) => resolveSchool(getValue()),
    }),
    columnHelper.accessor('route', {
      header: 'Route',
      size: 180,
      cell: ({ getValue, row }) => {
        const route = getValue();
        const label = routeLabels[route] || route || 'N/A';
        const hasInfant = row.original.has_infant;
        const infantName = hasInfant
          ? [row.original.infant_first_name, row.original.infant_last_name].filter(Boolean).join(' ')
          : '';
        return (
          <span>
            {label}
            {hasInfant && (
              <span
                className="ml-1 text-xs text-pink-600 font-medium"
                title={infantName ? `Infant: ${infantName}` : 'Has infant'}
              >
                (+infant)
              </span>
            )}
          </span>
        );
      },
    }),
    columnHelper.accessor('birth_date', {
      header: 'Birth Date',
      size: 100,
    }),
    columnHelper.accessor('passport_number', {
      header: 'Passport #',
      size: 120,
    }),
    columnHelper.accessor('passport_country', {
      header: 'Country',
      size: 90,
    }),
    columnHelper.accessor('passport_expiration', {
      header: 'Passport Exp',
      size: 100,
    }),
    columnHelper.accessor('amount_charged', {
      header: 'Amount',
      size: 100,
      cell: ({ getValue, row }) => {
        const amountCharged = getValue();
        const paymentStatus = row.original.stripe_payment_status;
        const route = row.original.route;

        if (paymentStatus === 'succeeded' && amountCharged) {
          return (
            <span className="text-green-600 font-medium text-sm">
              ${parseFloat(amountCharged).toFixed(0)}
            </span>
          );
        }

        const flightPrices = routePricesByFlight[row.original.flight_id] || routePricesByFlight['flight-2'];
        const basePrice = flightPrices[route];
        if (basePrice) {
          const expectedPrice = row.original.has_infant ? basePrice + INFANT_SURCHARGE : basePrice;
          return <span className="text-gray-400 text-sm">${expectedPrice}</span>;
        }
        return <span className="text-gray-400 text-sm">-</span>;
      },
    }),
    columnHelper.accessor('tariff_paid', {
      header: 'Tariff',
      size: 80,
      cell: ({ getValue, row }) => {
        const val = getValue();
        const flightId = row.original.flight_id;
        const isPaid = row.original.stripe_payment_status === 'succeeded';
        const isDeleted = row.original.admin_status === 'delete';
        if (!['flight-2', 'flight-3'].includes(flightId) || !isPaid || isDeleted) return <span className="text-gray-300">-</span>;
        if (val === 'paid-card') return <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">CARD</span>;
        if (val === 'paid-cash') return <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">CASH</span>;
        if (val === 'exempt') return <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">EXEMPT</span>;
        return <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">UNPAID</span>;
      },
    }),
    columnHelper.accessor('created_at', {
      header: 'Created',
      size: 160,
      cell: ({ getValue }) => {
        const date = new Date(getValue());
        return date.toLocaleString();
      },
    }),
    columnHelper.accessor('passport_s3_key', {
      header: 'Passport',
      size: 100,
      enableSorting: false,
      cell: ({ getValue, row }) => {
        const s3Key = getValue();
        if (!s3Key) return 'No file';

        return (
          <button
            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm"
            onClick={() => viewPassport(row.original.id, `${row.original.first_name} ${row.original.last_name}`, row.original)}
          >
            View
          </button>
        );
      },
    }),
    columnHelper.accessor('ai_validation_status', {
      header: 'AI Status',
      size: 100,
      enableSorting: true,
      cell: ({ getValue, row }) => {
        const status = getValue();
        const aiComment = row.original.ai_validation_comment;

        if (!status) return <span className="text-gray-400 text-xs">Pending</span>;

        const statusColors = {
          'valid': 'bg-green-100 text-green-800',
          'invalid': 'bg-red-100 text-red-800',
          'unclear': 'bg-yellow-100 text-yellow-800',
          'pending': 'bg-blue-100 text-blue-800',
          'error': 'bg-gray-100 text-gray-800'
        };

        const colorClass = statusColors[status.toLowerCase()] || 'bg-gray-100 text-gray-800';

        const getTooltipText = () => {
          if (!aiComment) return undefined;

          try {
            const parsed = JSON.parse(aiComment);
            let tooltipText = `Confidence: ${parsed.confidence || 'N/A'}\n`;

            if (parsed.comparison) {
              tooltipText += '\nField Comparison:\n';
              Object.entries(parsed.comparison).forEach(([field, status]) => {
                tooltipText += `• ${field}: ${status}\n`;
              });
            }

            if (parsed.comments) {
              tooltipText += `\nComments: ${parsed.comments}`;
            }

            return tooltipText;
          } catch {
            return aiComment;
          }
        };

        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${colorClass} cursor-help`}
                title={getTooltipText()}>
            {status.toUpperCase()}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      size: 120,
      cell: ({ row }) => {
        const reg = row.original;
        const fConfig = flightConfigs[reg.flight_id];
        const isPaid = reg.stripe_payment_status === 'succeeded';
        const canCancel = isPaid
          && fConfig?.cancelDeadlines
          && !['cancel-pending', 'cancel-confirmed', 'delete'].includes(reg.admin_status);
        const isCancelled = ['cancel-pending', 'cancel-confirmed'].includes(reg.admin_status);

        return (
          <div className="flex gap-1 items-center">
            {/* Edit */}
            <button
              className="bg-yellow-500 hover:bg-yellow-600 text-white w-7 h-7 rounded flex items-center justify-center"
              onClick={() => openEditModal(reg)}
              title="Edit passenger details"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
            </button>
            {/* Ticket Actions */}
            {isPaid && reg.admin_status !== 'delete' && (
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white w-7 h-7 rounded flex items-center justify-center"
                onClick={() => setTicketActionReg(reg)}
                title="Ticket actions (cancel/transfer/replace)"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg>
              </button>
            )}
            {/* Send Cancel Request */}
            {canCancel && (
              <button
                className="bg-red-500 hover:bg-red-600 text-white w-7 h-7 rounded flex items-center justify-center"
                onClick={() => handleInitiateCancelClick(reg)}
                title="Send cancellation request email"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            )}
            {/* Un-cancel (reinstate) */}
            {isCancelled && (
              <button
                className="bg-green-500 hover:bg-green-600 text-white w-7 h-7 rounded flex items-center justify-center"
                onClick={() => handleUncancel(reg)}
                title="Reinstate — undo cancellation"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a5 5 0 015 5v2M3 10l4-4m-4 4l4 4" /></svg>
              </button>
            )}
          </div>
        );
      },
    }),
  ], [schoolIdMap]);

  // Filtered data based on school filter, status filter, and deleted status
  const filteredData = useMemo(() => {
    if (!registrationsQuery.data?.registrations) return [];
    
    let filtered = registrationsQuery.data.registrations;
    
    // Filter out deleted registrations unless showDeleted is true
    if (!showDeleted) {
      filtered = filtered.filter(reg => (reg.admin_status || 'pending') !== 'delete');
    }
    
    if (schoolFilter) {
      filtered = filtered.filter(reg => {
        return resolveSchool(reg.school).toLowerCase().includes(schoolFilter.toLowerCase());
      });
    }
    
    if (statusFilter) {
      filtered = filtered.filter(reg => {
        const currentStatus = reg.admin_status || 'pending';
        return currentStatus.toLowerCase() === statusFilter.toLowerCase();
      });
    }
    
    if (paymentStatusFilter) {
      if (paymentStatusFilter === 'cancel-confirmed') {
        filtered = filtered.filter(reg => reg.admin_status === 'cancel-confirmed');
      } else if (paymentStatusFilter === 'cancel-pending') {
        filtered = filtered.filter(reg => reg.admin_status === 'cancel-pending');
      } else if (paymentStatusFilter === 'refundable') {
        filtered = filtered.filter(reg => reg.admin_status === 'cancel-confirmed' && refundableRegIds.has(reg.id));
      } else {
        filtered = filtered.filter(reg => {
          const currentPaymentStatus = reg.stripe_payment_status || 'pending';
          return currentPaymentStatus.toLowerCase() === paymentStatusFilter.toLowerCase();
        });
      }
    }

    if (flightFilter) {
      filtered = filtered.filter(reg => reg.flight_id === flightFilter);
    }

    if (dedupeByPassport) {
      const seen = new Map();
      // Sort by created_at DESC so we keep the most recent per passport
      const sorted = [...filtered].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      for (const reg of sorted) {
        const key = (reg.passport_number || '').toLowerCase().trim();
        if (key && !seen.has(key)) {
          seen.set(key, reg);
        } else if (!key) {
          seen.set(`id:${reg.id}`, reg);
        }
      }
      filtered = Array.from(seen.values());
    }

    return filtered;
  }, [registrationsQuery.data?.registrations, schoolFilter, statusFilter, paymentStatusFilter, showDeleted, flightFilter, dedupeByPassport, refundableRegIds]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: 'includesString',
    initialState: {
      sorting: [
        {
          id: 'created_at',
          desc: true,
        },
      ],
    },
  });

  // Virtualization setup
  const tableContainerRef = useRef(null);
  
  const rowVirtualizer = useVirtualizer({
    count: table.getRowModel().rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 60, // Estimated row height
    overscan: 10,
  });


  const viewPassport = (registrationId, studentName, rowData) => {
    setSelectedPassport({
      name: `${studentName} - Passport`,
      registrationId: registrationId,
      details: {
        firstName: rowData.first_name,
        lastName: rowData.last_name,
        birthDate: rowData.birth_date,
        passportNumber: rowData.passport_number,
        email: rowData.email,
        phone: rowData.phone,
        registrationCode: rowData.registration_code
      },
      aiValidation: {
        status: rowData.ai_validation_status,
        comment: rowData.ai_validation_comment,
        validatedAt: rowData.ai_validation_at
      }
    });
    setShowPassportModal(true);
  };

  const downloadPassport = async (registrationId, studentName) => {
    try {
      toast.info('Generating download link...', { autoClose: 2000 });
      
      const response = await apiFetch(
        `${import.meta.env.VITE_API_URL}/flight-admin/passport/${registrationId}?access_code=${accessCode}&download=true`
      );
      
      if (response.status === 'success') {
        const link = document.createElement('a');
        link.href = response.url;
        link.download = `${studentName.replace(/\s+/g, '_')}_passport.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Download started');
      } else {
        toast.error('Failed to generate download link');
      }
    } catch (error) {
      console.error('Error downloading passport:', error);
      toast.error(`Failed to download passport: ${error.message}`);
    }
  };


  // Capitalize first letter for gender select matching
  const normalizeGender = (g) => {
    if (!g) return '';
    const lower = g.toLowerCase();
    if (lower === 'male') return 'Male';
    if (lower === 'female') return 'Female';
    return g;
  };

  const openEditModal = (reg) => {
    setEditingReg(reg);
    setEditFields({
      first_name: reg.first_name || '',
      last_name: reg.last_name || '',
      birth_date: reg.birth_date || '',
      gender: normalizeGender(reg.gender),
      email: reg.email || '',
      phone: reg.phone || '',
      school: reg.school || '',
      passport_number: reg.passport_number || '',
      passport_country: reg.passport_country || '',
      passport_expiration: reg.passport_expiration || '',
      ...(reg.has_infant ? {
        infant_first_name: reg.infant_first_name || '',
        infant_last_name: reg.infant_last_name || '',
        infant_birth_date: reg.infant_birth_date || '',
        infant_gender: normalizeGender(reg.infant_gender),
        infant_passport_number: reg.infant_passport_number || '',
        infant_passport_country: reg.infant_passport_country || '',
        infant_passport_expiration: reg.infant_passport_expiration || '',
      } : {}),
    });
  };

  const saveEdit = async () => {
    if (!editingReg) return;
    // Build only changed fields
    const changed = {};
    const genderKeys = new Set(['gender', 'infant_gender']);
    for (const [key, val] of Object.entries(editFields)) {
      const original = editingReg[key] || '';
      const isDiff = genderKeys.has(key)
        ? val.toLowerCase() !== original.toLowerCase()
        : val !== original;
      if (isDiff) changed[key] = val;
    }
    if (Object.keys(changed).length === 0) {
      toast.info('No changes to save');
      setEditingReg(null);
      return;
    }
    setEditSaving(true);
    try {
      const res = await apiFetch(`${import.meta.env.VITE_API_URL}/flight-admin/update-passenger?access_code=${accessCode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ registrationId: editingReg.id, fields: changed }),
      });
      if (res.status === 'success') {
        toast.success(`Updated ${Object.keys(changed).join(', ')}`);
        registrationsQuery.refetch();
        setEditingReg(null);
      } else {
        toast.error(res.message || 'Update failed');
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setEditSaving(false);
    }
  };

  const handleInitiateCancelClick = async (reg) => {
    const confirmed = window.confirm(
      `Send cancellation request to ${reg.first_name} ${reg.last_name} (${reg.email})?\n\nThis will email them a link to confirm their cancellation.`
    );
    if (!confirmed) return;

    try {
      const res = await apiFetch(`${import.meta.env.VITE_API_URL}/flight-admin/initiate-cancel?access_code=${accessCode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ registrationId: reg.id }),
      });
      if (res.status === 'success') {
        toast.success(`Cancellation email sent to ${reg.email}`);
        registrationsQuery.refetch();
      } else {
        toast.error(res.message || 'Failed to initiate cancellation');
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    }
  };

  const handleUncancel = async (reg) => {
    const confirmed = window.confirm(
      `Reinstate ${reg.first_name} ${reg.last_name}?\n\nThis will reset their status back to pending (active booking).`
    );
    if (!confirmed) return;
    try {
      const res = await apiFetch(`${import.meta.env.VITE_API_URL}/flight-admin/update-passenger?access_code=${accessCode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ registrationId: reg.id, fields: { admin_status: 'pending', cancel_token: null, cancel_confirmed_at: null, cancel_last4: null, admin_comment: 'Reinstated via admin panel' } }),
      });
      if (res.status === 'success') {
        toast.success(`${reg.first_name} ${reg.last_name} reinstated`);
        registrationsQuery.refetch();
      } else {
        toast.error(res.message || 'Failed to reinstate');
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    }
  };

  // Show loading state
  if (authQuery.isLoading && accessCode) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Verifying access...</p>
        </div>
      </div>
    );
  }
  
  // Show unauthorized if authentication failed or no access code
  if (!authenticated || !accessCode) {
    return (
      <div className="max-w-md mx-auto mt-16 p-6 bg-white rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Flight Admin Access</h2>
        {authError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {authError}
          </div>
        )}
        <form onSubmit={(e) => {
          e.preventDefault();
          const formData = new FormData(e.target);
          updateAccessCode(formData.get('access_code'));
        }}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Access Code
            </label>
            <input
              type="password"
              name="access_code"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              placeholder="Enter admin access code"
              required
            />
          </div>
          <button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Access Admin Panel
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-4 py-8">
        <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Flight Registration Admin Panel
            </h1>
            {userInfo && (
              <p className="text-gray-600">
                Welcome, {userInfo.name || 'Administrator'}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <a
              href={`/buses?access_code=${accessCode}`}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Bus Manager (Admin)
            </a>
            <a
              href="/buses?access_code=igud-bus-2026"
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
            >
              Bus Manager (View)
            </a>
          </div>
        </div>

        {/* Flight 1 Stats */}
        {(() => {
          const f1Regs = activeRegistrations.filter(r => r.flight_id === 'flight-1');
          const f1Paid = f1Regs.filter(r => r.stripe_payment_status === 'succeeded');
          return (
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
              <div className="text-sm font-semibold mb-3 px-2 py-1 rounded inline-block bg-sky-100 text-sky-800">
                Flight 1 — Mon March 16
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {f1Paid.length}
                    <span className="text-sm text-gray-400"> / {f1Config.totalCapacity}</span>
                    <ExportButton rows={f1Paid} filename="flight1-all-paid.csv" title="Export all Flight 1 paid" />
                  </div>
                  <div className="text-gray-600 text-sm">Total Paid</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {f1Paid.filter(r => !r.access_code).length}
                    <span className="text-sm text-gray-400"> / {f1Config.publicCapacity}</span>
                  </div>
                  <div className="text-gray-600 text-sm">General</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {f1Paid.filter(r => r.access_code).length}
                    <span className="text-sm text-gray-400"> / {f1Config.totalCapacity - f1Config.publicCapacity}</span>
                  </div>
                  <div className="text-gray-600 text-sm">Access Code</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {f1Paid.filter(r => r.route === 'jerusalem-milan' || r.route === 'milan').length}
                    <ExportButton rows={f1Paid.filter(r => r.route === 'jerusalem-milan' || r.route === 'milan')} filename="flight1-milan-paid.csv" title="Export Flight 1 Milan" />
                  </div>
                  <div className="text-gray-600 text-sm">Milan</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-indigo-600">
                    {f1Paid.filter(r => r.route === 'jerusalem-newyork' || r.route === 'newyork').length}
                    <span className="text-sm text-gray-400"> / {f1Config.nycCapacity}</span>
                    <ExportButton rows={f1Paid.filter(r => r.route === 'jerusalem-newyork' || r.route === 'newyork')} filename="flight1-nyc-paid.csv" title="Export Flight 1 NYC" />
                  </div>
                  <div className="text-gray-600 text-sm">NYC</div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Flight 2 Stats */}
        {(() => {
          const f2Regs = activeRegistrations.filter(r => r.flight_id === 'flight-2');
          const f2Paid = f2Regs.filter(r => r.stripe_payment_status === 'succeeded');
          return (
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
              <div className="text-sm font-semibold mb-3 px-2 py-1 rounded inline-block bg-amber-100 text-amber-800">
                Flight 2 — Thu March 19
              </div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {f2Paid.length}
                    <span className="text-sm text-gray-400"> / {f2Config.totalCapacity}</span>
                    <ExportButton rows={f2Paid} filename="flight2-all-paid.csv" title="Export all Flight 2 paid" />
                  </div>
                  <div className="text-gray-600 text-sm">Total Paid</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {f2Paid.filter(r => r.access_code && (r.access_code.startsWith('RSV-'))).length}
                    <span className="text-sm text-gray-400"> / {f2Codes.reservedTotal || 0}</span>
                  </div>
                  <div className="text-gray-600 text-sm">Reserved</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {f2Paid.filter(r => r.route === 'jerusalem-milan' || r.route === 'milan').length}
                    <span className="text-sm text-gray-400"> / {f2Config.milanCapacity ?? (f2Config.totalCapacity - f2Config.nycCapacity)}</span>
                    {(() => { const total = f2Paid.filter(r => r.route === 'jerusalem-milan' || r.route === 'milan').length; const cap = f2Config.milanCapacity ?? (f2Config.totalCapacity - f2Config.nycCapacity); const resold = Math.max(0, total - cap); return resold > 0 ? <span className="text-xs text-red-400 ml-1">+{resold} resold</span> : null; })()}
                    <ExportButton rows={f2Paid.filter(r => r.route === 'jerusalem-milan' || r.route === 'milan')} filename="flight2-milan-paid.csv" title="Export Flight 2 Milan" />
                  </div>
                  <div className="text-gray-600 text-sm">Milan</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {(() => { const cc = f2Paid.filter(r => r.admin_status === 'cancel-confirmed' && (r.route === 'jerusalem-milan' || r.route === 'milan')).length; const cp = f2Paid.filter(r => r.admin_status === 'cancel-pending' && (r.route === 'jerusalem-milan' || r.route === 'milan')).length; return <>{cc}{cp > 0 && <span className="text-sm text-gray-400"> + {cp} pending</span>}</>; })()}
                  </div>
                  <div className="text-gray-600 text-sm">Cancelled Milan</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-indigo-600">
                    {f2Paid.filter(r => r.route === 'jerusalem-newyork' || r.route === 'newyork').length}
                    <span className="text-sm text-gray-400"> / {f2Config.nycCapacity}</span>
                    {(() => { const total = f2Paid.filter(r => r.route === 'jerusalem-newyork' || r.route === 'newyork').length; const resold = Math.max(0, total - f2Config.nycCapacity); return resold > 0 ? <span className="text-xs text-red-400 ml-1">+{resold} resold</span> : null; })()}
                    <ExportButton rows={f2Paid.filter(r => r.route === 'jerusalem-newyork' || r.route === 'newyork')} filename="flight2-nyc-paid.csv" title="Export Flight 2 NYC" />
                  </div>
                  <div className="text-gray-600 text-sm">NYC</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {(() => { const cc = f2Paid.filter(r => r.admin_status === 'cancel-confirmed' && (r.route === 'jerusalem-newyork' || r.route === 'newyork')).length; const cp = f2Paid.filter(r => r.admin_status === 'cancel-pending' && (r.route === 'jerusalem-newyork' || r.route === 'newyork')).length; return <>{cc}{cp > 0 && <span className="text-sm text-gray-400"> + {cp} pending</span>}</>; })()}
                  </div>
                  <div className="text-gray-600 text-sm">Cancelled NYC</div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Flight 3 Stats */}
        {(() => {
          const f3Regs = activeRegistrations.filter(r => r.flight_id === 'flight-3');
          const f3Paid = f3Regs.filter(r => r.stripe_payment_status === 'succeeded');
          return (
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
              <div className="text-sm font-semibold mb-3 px-2 py-1 rounded inline-block bg-emerald-100 text-emerald-800">
                Flight 3 — Tue March 17 (Banquest)
              </div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-4 overflow-visible">
                <div className="text-center overflow-visible">
                  <div className="text-2xl font-bold text-blue-600 whitespace-nowrap">
                    {f3Paid.length}
                    <span className="text-sm text-gray-400"> / {f3Config.totalCapacity}</span>
                    <ExportButton rows={f3Paid} filename="flight3-all-paid.csv" title="Export all Flight 3 paid" />
                  </div>
                  <div className="text-gray-600 text-sm">Total Paid</div>
                </div>
                <div className="text-center overflow-visible">
                  <div className="text-2xl font-bold text-blue-600 whitespace-nowrap">
                    {f3Paid.filter(r => r.access_code && (r.access_code.startsWith('RSV-'))).length}
                    <span className="text-sm text-gray-400"> / {f3Codes.reservedTotal || 0}</span>
                  </div>
                  <div className="text-gray-600 text-sm">Reserved</div>
                </div>
                <div className="text-center overflow-visible">
                  <div className="text-2xl font-bold text-purple-600 whitespace-nowrap">
                    {f3Paid.filter(r => r.route === 'jerusalem-milan' || r.route === 'milan').length}
                    <span className="text-sm text-gray-400"> / {f3Config.milanCapacity}</span>
                    {(() => { const total = f3Paid.filter(r => r.route === 'jerusalem-milan' || r.route === 'milan').length; const resold = Math.max(0, total - f3Config.milanCapacity); return resold > 0 ? <span className="text-xs text-red-400 ml-1">+{resold} resold</span> : null; })()}
                    <ExportButton rows={f3Paid.filter(r => r.route === 'jerusalem-milan' || r.route === 'milan')} filename="flight3-milan-paid.csv" title="Export Flight 3 Milan" />
                  </div>
                  <div className="text-gray-600 text-sm">Milan</div>
                </div>
                <div className="text-center overflow-visible">
                  <div className="text-2xl font-bold text-red-600 whitespace-nowrap">
                    {(() => { const cc = f3Paid.filter(r => r.admin_status === 'cancel-confirmed' && (r.route === 'jerusalem-milan' || r.route === 'milan')).length; const cp = f3Paid.filter(r => r.admin_status === 'cancel-pending' && (r.route === 'jerusalem-milan' || r.route === 'milan')).length; return <>{cc}{cp > 0 && <span className="text-sm text-gray-400"> + {cp} pending</span>}</>; })()}
                  </div>
                  <div className="text-gray-600 text-sm">Cancelled Milan</div>
                </div>
                <div className="text-center overflow-visible">
                  <div className="text-2xl font-bold text-indigo-600 whitespace-nowrap">
                    {f3Paid.filter(r => r.route === 'jerusalem-newyork' || r.route === 'newyork').length}
                    <span className="text-sm text-gray-400"> / {f3Config.nycCapacity}</span>
                    {(() => { const total = f3Paid.filter(r => r.route === 'jerusalem-newyork' || r.route === 'newyork').length; const resold = Math.max(0, total - f3Config.nycCapacity); return resold > 0 ? <span className="text-xs text-red-400 ml-1">+{resold} resold</span> : null; })()}
                    <ExportButton rows={f3Paid.filter(r => r.route === 'jerusalem-newyork' || r.route === 'newyork')} filename="flight3-nyc-paid.csv" title="Export Flight 3 NYC" />
                  </div>
                  <div className="text-gray-600 text-sm">NYC</div>
                </div>
                <div className="text-center overflow-visible">
                  <div className="text-2xl font-bold text-red-600 whitespace-nowrap">
                    {(() => { const cc = f3Paid.filter(r => r.admin_status === 'cancel-confirmed' && (r.route === 'jerusalem-newyork' || r.route === 'newyork')).length; const cp = f3Paid.filter(r => r.admin_status === 'cancel-pending' && (r.route === 'jerusalem-newyork' || r.route === 'newyork')).length; return <>{cc}{cp > 0 && <span className="text-sm text-gray-400"> + {cp} pending</span>}</>; })()}
                  </div>
                  <div className="text-gray-600 text-sm">Cancelled NYC</div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Waitlist & Totals */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {(() => {
              const paidRegs = activeRegistrations.filter(r => r.stripe_payment_status === 'succeeded');
              const totalCapacity = f1Config.totalCapacity + f2Config.totalCapacity + f3Config.totalCapacity;
              return (
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {paidRegs.length}
                    <span className="text-sm text-gray-400"> / {totalCapacity}</span>
                  </div>
                  <div className="text-gray-600 text-sm">Total Paid (All)</div>
                </div>
              );
            })()}
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{multiFlightCodes.waitlist}</div>
              <div className="text-gray-600 text-sm">Multi-Flight In Cycle</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {stillLookingStats.total}
              </div>
              <div className="text-gray-600 text-sm">Still Looking</div>
              <div className="text-xs text-gray-500 mt-1">
                <span className="text-green-600">{stillLookingStats.committedToBook} committed</span>
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-600">
                {activeRegistrations.filter(r => r.ai_validation_status === 'valid').length}
              </div>
              <div className="text-gray-600 text-sm">AI Validated (All)</div>
              <div className="text-xs text-gray-500 mt-1">
                <span className="text-red-500">{activeRegistrations.filter(r => r.ai_validation_status === 'invalid').length} Invalid</span>
                {' • '}
                <span className="text-gray-400">{activeRegistrations.filter(r => !r.ai_validation_status || r.ai_validation_status === 'pending').length} Pending</span>
              </div>
            </div>
          </div>
        </div>

        {/* Waitlist Queue */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-800">Waitlist Queue</h2>
            <button
              onClick={() => setShowWaitlistQueue(!showWaitlistQueue)}
              className="text-sm px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-700"
            >
              {showWaitlistQueue ? 'Hide' : 'Show'} Queue
            </button>
          </div>

          {showWaitlistQueue && (
            <>
              <div className="mb-3">
                <input
                  type="text"
                  value={waitlistSearch}
                  onChange={(e) => setWaitlistSearch(e.target.value)}
                  placeholder="Search by name, email, ID, passport, code..."
                  className="w-full md:w-96 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              {(() => {
                const searchTerm = waitlistSearch.toLowerCase().trim();
                const matchesSearch = (p) => {
                  if (!searchTerm) return true;
                  return [
                    String(p.id),
                    String(p.position),
                    p.firstName || '',
                    p.lastName || '',
                    `${p.firstName || ''} ${p.lastName || ''}`,
                    p.email || '',
                    p.passportNumber || '',
                    p.accessCode || '',
                    p.code || '',
                    p.codeStatus || '',
                  ].some(val => val.toLowerCase().includes(searchTerm));
                };

                const milanQueue = waitlistQueue.filter(p => (p.route === 'jerusalem-milan' || p.route === 'milan') && matchesSearch(p));
                const nycQueue = waitlistQueue.filter(p => (p.route === 'jerusalem-newyork' || p.route === 'newyork') && matchesSearch(p));

                const renderQueue = (items, label) => {
                  if (!items.length) return <p className="text-sm text-gray-400 mb-2">No {label} waitlist entries</p>;
                  const activeCount = items.filter(p => p.codeStatus === 'active').length;
                  return (
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold text-gray-600 mb-1">
                        {label} ({items.length})
                        {activeCount > 0 && <span className="ml-2 text-green-600">{activeCount} active</span>}
                      </h3>
                      <div className="overflow-x-auto max-h-72 overflow-y-auto border rounded">
                        <table className="min-w-full text-xs">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-2 py-1 text-left font-medium text-gray-500">#</th>
                              <th className="px-2 py-1 text-left font-medium text-gray-500">Name</th>
                              <th className="px-2 py-1 text-left font-medium text-gray-500">Email</th>
                              <th className="px-2 py-1 text-left font-medium text-gray-500">Committed</th>
                              <th className="px-2 py-1 text-left font-medium text-gray-500">Registered</th>
                              <th className="px-2 py-1 text-left font-medium text-gray-500">Code Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((p, idx) => {
                              const statusColors = {
                                active: 'bg-green-100 text-green-800',
                                paid: 'bg-blue-100 text-blue-800',
                                expired: 'bg-orange-100 text-orange-800',
                                declined: 'bg-red-100 text-red-800',
                              };
                              return (
                                <tr key={p.id} className={`border-t ${p.codeStatus === 'active' ? 'bg-green-50' : ''}`}>
                                  <td className="px-2 py-1 text-gray-500">{idx + 1}</td>
                                  <td className="px-2 py-1 font-medium">{p.firstName} {p.lastName}</td>
                                  <td className="px-2 py-1 text-gray-600">{p.email}</td>
                                  <td className="px-2 py-1">
                                    {p.committedToBook
                                      ? <span className="text-green-600 font-medium">Yes</span>
                                      : <span className="text-gray-400">No</span>}
                                  </td>
                                  <td className="px-2 py-1 text-gray-500">{new Date(p.createdAt).toLocaleString()}</td>
                                  <td className="px-2 py-1">
                                    {p.codeStatus ? (
                                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${statusColors[p.codeStatus] || 'bg-gray-100 text-gray-800'}`}
                                        title={p.code ? `${p.code}${p.codeExpiresAt ? ` — expires ${new Date(p.codeExpiresAt).toLocaleString()}` : ''}` : ''}>
                                        {p.codeStatus.toUpperCase()}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400">Waiting</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                };

                return (
                  <div>
                    {renderQueue(milanQueue, 'Milan')}
                    {renderQueue(nycQueue, 'NYC')}
                  </div>
                );
              })()}
            </>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Flight
              </label>
              <select
                value={flightFilter}
                onChange={(e) => setFlightFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Flights</option>
                <option value="flight-1">Flight 1 — Mon March 16</option>
                <option value="flight-2">Flight 2 — Thu March 19</option>
                <option value="flight-3">Flight 3 — Tue March 17</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search All Fields
              </label>
              <input
                type="text"
                value={globalFilter ?? ''}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Search registrations..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filter by School
              </label>
              <select
                value={schoolFilter}
                onChange={(e) => setSchoolFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Schools</option>
                {schools.map((school) => (
                  <option key={school} value={school}>
                    {school}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Payment Status
              </label>
              <select
                value={paymentStatusFilter}
                onChange={(e) => setPaymentStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Payment Statuses</option>
                {paymentStatusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'None'}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Options
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showDeleted}
                  onChange={(e) => setShowDeleted(e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <span className="text-sm font-medium text-gray-700">
                  Show Deleted ({registrationsQuery.data?.registrations?.filter(r => r.admin_status === 'delete').length || 0})
                </span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer mt-1">
                <input
                  type="checkbox"
                  checked={dedupeByPassport}
                  onChange={(e) => setDedupeByPassport(e.target.checked)}
                  className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                />
                <span className="text-sm font-medium text-gray-700">
                  Dedupe by Passport
                </span>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Actions
              </label>
              <button
                onClick={() => {
                  setGlobalFilter('');
                  setSchoolFilter('');
                  setStatusFilter('');
                  setPaymentStatusFilter('');
                  setFlightFilter('');
                  setShowDeleted(false);
                  setDedupeByPassport(false);
                }}
                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md"
              >
                Clear Filters
              </button>
              <button
                onClick={() => setShowAddPassenger(true)}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                Add Passenger
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {registrationsQuery.isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p>Loading registrations...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {registrationsQuery.isError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            Error loading registrations: {registrationsQuery.error?.message}
          </div>
        )}

        {/* Virtualized Table */}
        {registrationsQuery.data && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="min-w-full">
              {/* Virtual Table Container */}
              <div
                ref={tableContainerRef}
                className="overflow-auto"
                style={{ height: '600px' }}
              >
                {/* Table Header - Sticky */}
                <div className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
                  {table.getHeaderGroups().map(headerGroup => {
                    const totalWidth = headerGroup.headers.reduce((sum, header) => sum + header.getSize(), 0);
                    return (
                      <div
                        key={headerGroup.id}
                        className="flex bg-gray-50"
                        style={{ minWidth: `${totalWidth}px` }}
                      >
                        {headerGroup.headers.map(header => (
                          <div
                            key={header.id}
                            className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-r border-gray-200 last:border-r-0 flex items-center bg-gray-50"
                            style={{ width: header.getSize(), minWidth: header.getSize() }}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            <div className="flex items-center space-x-1">
                              <span>
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </span>
                              <span className="text-gray-400">
                                {{
                                  asc: '↑',
                                  desc: '↓',
                                }[header.column.getIsSorted()] ?? '↕'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>

                {/* Virtual Table Body */}
                <div
                  style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const row = table.getRowModel().rows[virtualRow.index];
                    return (
                      <div
                        key={row.id}
                        className="absolute top-0 left-0 w-full flex hover:bg-gray-50 border-b border-gray-200"
                        style={{
                          height: `${virtualRow.size}px`,
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        {row.getVisibleCells().map(cell => (
                          <div
                            key={cell.id}
                            className="px-3 py-4 text-sm text-gray-900 border-r border-gray-200 last:border-r-0 flex items-center"
                            style={{
                              width: cell.column.getSize(),
                              minWidth: cell.column.getSize(),
                              wordBreak: 'break-word'
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Results Summary */}
              <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{table.getRowModel().rows.length}</span> registrations
                  {globalFilter || schoolFilter || statusFilter || flightFilter || showDeleted ? (
                    <span>
                      {' '}(filtered from{' '}
                      <span className="font-medium">{showDeleted ? registrationsQuery.data?.registrations?.length || 0 : activeRegistrations.length}</span> {showDeleted ? 'total' : 'active'})
                    </span>
                  ) : null}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Passenger Modal */}
      {editingReg && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 opacity-75" onClick={() => !editSaving && setEditingReg(null)}></div>
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  Edit Passenger — ID {editingReg.id} ({editingReg.registration_code})
                </h3>
                <button onClick={() => !editSaving && setEditingReg(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    ['first_name', 'First Name'],
                    ['last_name', 'Last Name'],
                    ['birth_date', 'Birth Date (DD/MM/YYYY)'],
                    ['gender', 'Gender'],
                    ['email', 'Email'],
                    ['phone', 'Phone'],
                    ['school', 'School'],
                    ['passport_number', 'Passport Number'],
                    ['passport_country', 'Passport Country'],
                    ['passport_expiration', 'Passport Expiration'],
                  ].map(([key, label]) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                      {key === 'gender' ? (
                        <select
                          value={editFields[key] || ''}
                          onChange={e => setEditFields(f => ({ ...f, [key]: e.target.value }))}
                          className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 ${
                            (editFields[key] || '') !== (editingReg[key] || '') ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                          }`}
                        >
                          <option value="">--</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={editFields[key] || ''}
                          onChange={e => setEditFields(f => ({ ...f, [key]: e.target.value }))}
                          className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 ${
                            (editFields[key] || '') !== (editingReg[key] || '') ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                          }`}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* Infant fields (only if has_infant) */}
                {editingReg.has_infant && (
                  <>
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-semibold text-pink-700 mb-3">Infant Details</h4>
                      <div className="grid grid-cols-2 gap-4">
                        {[
                          ['infant_first_name', 'First Name'],
                          ['infant_last_name', 'Last Name'],
                          ['infant_birth_date', 'Birth Date'],
                          ['infant_gender', 'Gender'],
                          ['infant_passport_number', 'Passport Number'],
                          ['infant_passport_country', 'Passport Country'],
                          ['infant_passport_expiration', 'Passport Expiration'],
                        ].map(([key, label]) => (
                          <div key={key}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                            {key === 'infant_gender' ? (
                              <select
                                value={editFields[key] || ''}
                                onChange={e => setEditFields(f => ({ ...f, [key]: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">--</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                              </select>
                            ) : (
                              <input
                                type="text"
                                value={editFields[key] || ''}
                                onChange={e => setEditFields(f => ({ ...f, [key]: e.target.value }))}
                                className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 ${
                                  (editFields[key] || '') !== (editingReg[key] || '') ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                                }`}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => setEditingReg(null)}
                  disabled={editSaving}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={saveEdit}
                  disabled={editSaving}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm disabled:opacity-50"
                >
                  {editSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Passport Preview Modal */}
      {showPassportModal && selectedPassport && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-none sm:w-full sm:h-full max-h-screen">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {selectedPassport.name}
                  </h3>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => downloadPassport(selectedPassport.registrationId, selectedPassport.name)}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                    >
                      Download
                    </button>
                    <button
                      onClick={() => setShowPassportModal(false)}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                    >
                      Close
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                  {/* Passport Details Panel */}
                  <div className="space-y-4">
                    {/* Registration Details */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-md font-semibold text-gray-800 mb-3">Registration Details</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-600">Registration Code:</span>
                          <span className="text-gray-900">{selectedPassport.details?.registrationCode}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-600">First Name:</span>
                          <span className="text-gray-900">{selectedPassport.details?.firstName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-600">Last Name:</span>
                          <span className="text-gray-900">{selectedPassport.details?.lastName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-600">Birth Date:</span>
                          <span className="text-gray-900">{selectedPassport.details?.birthDate}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-600">Passport Number:</span>
                          <span className="text-gray-900 font-mono">{selectedPassport.details?.passportNumber}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-600">Email:</span>
                          <span className="text-gray-900">{selectedPassport.details?.email}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-600">Phone:</span>
                          <span className="text-gray-900">{selectedPassport.details?.phone}</span>
                        </div>
                      </div>
                    </div>

                    {/* AI Validation Results */}
                    {selectedPassport.aiValidation && (
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="text-md font-semibold text-gray-800 mb-3">AI Validation Status</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-gray-600">Status:</span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              selectedPassport.aiValidation.status === 'valid' ? 'bg-green-100 text-green-800' :
                              selectedPassport.aiValidation.status === 'invalid' ? 'bg-red-100 text-red-800' :
                              selectedPassport.aiValidation.status === 'unclear' ? 'bg-yellow-100 text-yellow-800' :
                              selectedPassport.aiValidation.status === 'pending' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {selectedPassport.aiValidation.status?.toUpperCase() || 'PENDING'}
                            </span>
                          </div>
                          {selectedPassport.aiValidation.validatedAt && (
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-600">Validated:</span>
                              <span className="text-gray-900 text-xs">
                                {new Date(selectedPassport.aiValidation.validatedAt).toLocaleString()}
                              </span>
                            </div>
                          )}
                          {selectedPassport.aiValidation.comment && (
                            <div className="mt-3">
                              <span className="font-medium text-gray-600 block mb-1">AI Analysis:</span>
                              <div className="bg-white p-2 rounded text-xs max-h-32 overflow-y-auto">
                                {(() => {
                                  try {
                                    const parsed = JSON.parse(selectedPassport.aiValidation.comment);
                                    return (
                                      <div className="space-y-1">
                                        <div><strong>Confidence:</strong> {parsed.confidence}</div>
                                        {parsed.comparison && (
                                          <div className="space-y-1">
                                            <strong>Field Comparison:</strong>
                                            {Object.entries(parsed.comparison).map(([field, status]) => (
                                              <div key={field} className="ml-2">
                                                <span className="capitalize">{field}:</span>
                                                <span className={`ml-1 ${status === 'MATCH' ? 'text-green-600' : status === 'MISMATCH' ? 'text-red-600' : 'text-yellow-600'}`}>
                                                  {status}
                                                </span>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        {parsed.comments && (
                                          <div><strong>Comments:</strong> {parsed.comments}</div>
                                        )}
                                      </div>
                                    );
                                  } catch {
                                    return selectedPassport.aiValidation.comment;
                                  }
                                })()}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Passport Viewer Panel */}
                  <PassportViewer
                    registrationId={selectedPassport.registrationId}
                    accessCode={accessCode}
                    onDownload={() => downloadPassport(selectedPassport.registrationId, selectedPassport.name)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ticket Action Modal */}
      {ticketActionReg && (
        <TicketActionModal
          reg={ticketActionReg}
          flightConfigs={flightConfigs}
          accessCode={accessCode}
          onClose={() => setTicketActionReg(null)}
          onSuccess={() => registrationsQuery.refetch()}
        />
      )}

      {/* Add Passenger Modal */}
      {showAddPassenger && (
        <AddPassengerModal
          flightConfigs={flightConfigs}
          accessCode={accessCode}
          onClose={() => setShowAddPassenger(false)}
          onSuccess={() => registrationsQuery.refetch()}
        />
      )}

    </div>
  );
}

export default FlightAdmin;